import styled from 'styled-components';
import { motion } from 'framer-motion';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';

export const TimerContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg};
  width: 100%;
  margin: ${({ theme }) => theme.spacing.lg} 0;
  position: relative;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.md};
    margin: ${({ theme }) => theme.spacing.md} 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    padding: ${({ theme }) => theme.spacing.sm};
    margin: ${({ theme }) => theme.spacing.sm} 0;
  }
`;

export const TimerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 600px;
  gap: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: ${({ theme }) => theme.spacing.md};
    max-width: 500px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    gap: ${({ theme }) => theme.spacing.sm};
    max-width: 400px;
  }
`;

export const TimerItem = styled(motion.div)`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all ${({ theme }) => theme.transitions.normal};
  min-width: 120px;
  min-height: 140px;
  gap: ${({ theme }) => theme.spacing.sm};

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-width: 100px;
    min-height: 120px;
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    min-width: 80px;
    min-height: 100px;
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

export const TimeDisplay = styled.div`
  font-size: 2.2rem;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  line-height: 1;
  font-family: 'JetBrains Mono', monospace;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 2rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    font-size: 1.6rem;
  }
`;

export const TimeLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.8;
  text-align: center;
  display: block;
  margin-top: 8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    margin-top: 4px;
  }
`;

export const GradientDefs = styled.svg`
  position: absolute;
  width: 0;
  height: 0;
`;

export const StyledCountdownTimer = styled(CountdownCircleTimer)`
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4));
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.5));
    transform: scale(1.02);
  }
`;

// Improved Timer component with labels outside circle
export const ResponsiveTimer = ({ time, unit, duration, remainingTime, gradientId, ...props }) => {
  // Use fixed sizes to avoid rendering issues
  const timerSize = 80;
  const strokeWidth = 4;
  const strokeWidthInactive = 1.5;

  return (
    <TimerItem
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.1 }}
      whileHover={{ scale: 1.02 }}
    >
      {/* Timer Circle */}
      <StyledCountdownTimer
        key={time}
        isPlaying
        size={timerSize}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        trailStrokeWidth={strokeWidthInactive}
        trailColor={`url(#${gradientId}Inactive)`}
        duration={duration}
        initialRemainingTime={remainingTime}
        colors={`url(#${gradientId})`}
        rotation="clockwise"
        onComplete={() => ({ shouldRepeat: unit !== 'DAYS' })}
        {...props}
      >
        {() => (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%'
          }}>
            <TimeDisplay>{time}</TimeDisplay>
          </div>
        )}
      </StyledCountdownTimer>
      
      {/* Label below the circle */}
      <TimeLabel>{unit}</TimeLabel>
    </TimerItem>
  );
};

